import React, { useState, useContext } from 'react';
import axios from 'axios';
import { HerracContext } from '../../App';
import { FaCreditCard, FaLock, FaTimes, FaCheckCircle } from 'react-icons/fa';

/**
 * PaymentForm - Payriff ödəniş sistemi ilə balans artırma forması
 * 
 * Payriff API dokumentasiyası: https://docs.payriff.com/
 * 
 * QEYD: Bu placeholder koddur. Əsl Payriff credentials-ları əlavə edin:
 * - MERCHANT_ID
 * - SECRET_KEY
 * - API_URL
 */

const PaymentForm = ({ onClose }) => {
  const { user, getUserBalance } = useContext(HerracContext);

  // State Management
  const [amount, setAmount] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardHolder, setCardHolder] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Predefined amounts
  const quickAmounts = [10, 20, 50, 100, 200, 500];

  // Payriff Configuration (PLACEHOLDER - əsl məlumatları əlavə edin)
  const PAYRIFF_CONFIG = {
    merchantId: 'YOUR_MERCHANT_ID',
    apiUrl: 'https://api.payriff.com/api/v1',
    returnUrl: window.location.origin + '/cabinet',
    cancelUrl: window.location.origin + '/cabinet',
  };

  // Format card number (4-4-4-4)
  const formatCardNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    const formatted = cleaned.match(/.{1,4}/g)?.join(' ') || cleaned;
    return formatted.substring(0, 19); // 16 digits + 3 spaces
  };

  // Format expiry date (MM/YY)
  const formatExpiryDate = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return cleaned.substring(0, 2) + '/' + cleaned.substring(2, 4);
    }
    return cleaned;
  };

  // Validate form
  const validateForm = () => {
    if (!amount || parseFloat(amount) < 1) {
      setError('Minimum məbləğ 1 AZN olmalıdır');
      return false;
    }

    if (cardNumber.replace(/\s/g, '').length !== 16) {
      setError('Kart nömrəsi 16 rəqəmdən ibarət olmalıdır');
      return false;
    }

    if (!cardHolder.trim()) {
      setError('Kart sahibinin adını daxil edin');
      return false;
    }

    if (!expiryDate.match(/^\d{2}\/\d{2}$/)) {
      setError('Son istifadə tarixi düzgün deyil (MM/YY)');
      return false;
    }

    if (cvv.length !== 3) {
      setError('CVV 3 rəqəmdən ibarət olmalıdır');
      return false;
    }

    setError('');
    return true;
  };

  // Create Payriff payment session (PLACEHOLDER)
  const createPayriffSession = async (paymentData) => {
    try {
      // PLACEHOLDER - Əsl Payriff API çağırışı
      const response = await axios.post(
        `${PAYRIFF_CONFIG.apiUrl}/payment/create`,
        {
          merchantId: PAYRIFF_CONFIG.merchantId,
          amount: paymentData.amount * 100, // Qəpik-ə çevirmək
          currency: 'AZN',
          description: `Balans artırma - ${user.email}`,
          returnUrl: PAYRIFF_CONFIG.returnUrl,
          cancelUrl: PAYRIFF_CONFIG.cancelUrl,
          language: 'az',
          // Kart məlumatları (PCI DSS uyğun göndərilməlidir)
          card: {
            number: paymentData.cardNumber.replace(/\s/g, ''),
            holder: paymentData.cardHolder,
            expiry: paymentData.expiryDate,
            cvv: paymentData.cvv,
          },
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer YOUR_SECRET_KEY`, // Əsl secret key
          },
        }
      );

      return response.data;
    } catch (err) {
      console.error('Payriff API xətası:', err);
      throw new Error('Ödəniş xətası baş verdi');
    }
  };

  // Handle payment submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      // Step 1: Create Payriff payment session
      const paymentData = {
        amount: parseFloat(amount),
        cardNumber,
        cardHolder,
        expiryDate,
        cvv,
      };

      // PLACEHOLDER - Əsl Payriff inteqrasiyası
      const payriffResponse = await createPayriffSession(paymentData);

      // Step 2: Process payment and update user balance
      if (payriffResponse.status === 'success') {
        // Backend-ə ödəniş məlumatını göndər
        await axios.post('https://api.herracim.az/api/Payment/ProcessPayment', {
          userId: user.userId,
          amount: parseFloat(amount),
          transactionId: payriffResponse.transactionId,
          paymentMethod: 'payriff',
          status: 'completed',
        });

        // Update balance in context
        await getUserBalance();

        // Show success
        setSuccess(true);

        // Reset form after 2 seconds
        setTimeout(() => {
          resetForm();
          if (onClose) onClose();
        }, 2000);
      } else {
        throw new Error('Ödəniş uğursuz oldu');
      }
    } catch (err) {
      console.error('Ödəniş xətası:', err);
      setError(err.message || 'Ödəniş zamanı xəta baş verdi');
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setAmount('');
    setCardNumber('');
    setCardHolder('');
    setExpiryDate('');
    setCvv('');
    setError('');
    setSuccess(false);
  };

  // Handle quick amount selection
  const handleQuickAmount = (value) => {
    setAmount(value.toString());
  };

  // Success screen
  if (success) {
    return (
      <div className="payment-form">
        <div className="payment-success">
          <div className="payment-success__icon">
            <FaCheckCircle />
          </div>
          <h3 className="payment-success__title">Ödəniş uğurlu!</h3>
          <p className="payment-success__message">
            Balansınıza {amount} AZN əlavə edildi
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-form">
      <div className="payment-form__header">
        <div className="payment-form__title-wrapper">
          <FaCreditCard className="payment-form__icon" />
          <h3 className="payment-form__title">Balans Artır</h3>
        </div>
        {onClose && (
          <button
            className="payment-form__close"
            onClick={onClose}
            aria-label="Bağla"
          >
            <FaTimes />
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="payment-form__body">
        {/* Amount Selection */}
        <div className="payment-form__section">
          <label className="payment-form__label">
            Məbləğ (AZN) <span className="payment-form__required">*</span>
          </label>
          <input
            type="number"
            className="payment-form__input"
            placeholder="Məbləği daxil edin"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            min="1"
            step="0.01"
            required
          />
          <div className="payment-form__quick-amounts">
            {quickAmounts.map((value) => (
              <button
                key={value}
                type="button"
                className={`payment-form__quick-amount ${
                  amount === value.toString() ? 'active' : ''
                }`}
                onClick={() => handleQuickAmount(value)}
              >
                {value} AZN
              </button>
            ))}
          </div>
        </div>

        {/* Card Number */}
        <div className="payment-form__section">
          <label className="payment-form__label">
            Kart nömrəsi <span className="payment-form__required">*</span>
          </label>
          <input
            type="text"
            className="payment-form__input payment-form__input--card"
            placeholder="0000 0000 0000 0000"
            value={cardNumber}
            onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
            maxLength="19"
            required
          />
        </div>

        {/* Card Holder */}
        <div className="payment-form__section">
          <label className="payment-form__label">
            Kart sahibinin adı <span className="payment-form__required">*</span>
          </label>
          <input
            type="text"
            className="payment-form__input"
            placeholder="AD SOYAD"
            value={cardHolder}
            onChange={(e) => setCardHolder(e.target.value.toUpperCase())}
            required
          />
        </div>

        {/* Expiry Date & CVV */}
        <div className="payment-form__row">
          <div className="payment-form__section">
            <label className="payment-form__label">
              Son istifadə tarixi <span className="payment-form__required">*</span>
            </label>
            <input
              type="text"
              className="payment-form__input"
              placeholder="MM/YY"
              value={expiryDate}
              onChange={(e) => setExpiryDate(formatExpiryDate(e.target.value))}
              maxLength="5"
              required
            />
          </div>
          <div className="payment-form__section">
            <label className="payment-form__label">
              CVV <span className="payment-form__required">*</span>
            </label>
            <input
              type="password"
              className="payment-form__input"
              placeholder="123"
              value={cvv}
              onChange={(e) => setCvv(e.target.value.replace(/\D/g, ''))}
              maxLength="3"
              required
            />
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="payment-form__error">
            <span>⚠️</span>
            {error}
          </div>
        )}

        {/* Security Notice */}
        <div className="payment-form__security">
          <FaLock />
          <span>Ödənişlər Payriff tərəfindən təhlükəsiz şəkildə həyata keçirilir</span>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="payment-form__submit"
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="payment-form__spinner"></span>
              Ödəniş edilir...
            </>
          ) : (
            <>
              <FaCreditCard />
              Ödənişi tamamla
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default PaymentForm;